import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  BookWidgetSearchModalComponent,
  PredictiveSearch,
  RecentListItem
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import {
  GenericAction,
  MultiRoutePickerComponent,
  MultiRoutePickerError,
  SelectedData,
  Tab,
  ToAndFromAirport,
  ToAndFromAirportCities
} from '@com-delta-omnichannel/mach';
import { Results } from '@com-delta-omnichannel/mach/lib/components/organisms/search-modal';
import {
  PredictiveSearchItemSelectionEventData,
  SearchModalEvent,
  SearchModalEventType,
  TriggerLocation
} from '../../../models/search-modal-events-object.interface';
import { ErrorStateDirective, SwapIconClickDirective } from '../directive';

@Component({
  standalone: true,
  selector: 'idp-multicity-route-picker',
  templateUrl: './multicity-route-picker.component.html',
  styleUrls: ['./multicity-route-picker.component.scss'],
  imports: [
    MultiRoutePickerComponent,
    SwapIconClickDirective,
    BookWidgetSearchModalComponent,
    ErrorStateDirective
  ]
})
export class MulticityRoutePickerComponent {
  private lastSearchModalTriggerLocation: TriggerLocation | undefined;
  protected modalOpen = false;
  protected error: MultiRoutePickerError = {
    destination: false,
    origin: false,
    sameRoute: false,
    originErrorMessage: '',
    destinationErrorMessage: '',
    sameRouteErrorMessage: ''
  };

  @Input() routePickerContent: ToAndFromAirport = {
    origin: {},
    destination: {},
    ariaLabels: {}
  } as ToAndFromAirport;

  @Input() routePickerCities: ToAndFromAirportCities = {
    destinationCode: '',
    destinationName: '',
    destinationRegion: '',
    originCode: '',
    originName: '',
    originRegion: ''
  } as ToAndFromAirportCities;

  @Input() routePickerSearchResult: Results = {} as Results;
  @Input() recentSearchList: RecentListItem[] = [];
  @Input() savedSearchList: string[] = [];
  @Input() searchModalTabs: Tab[] = [];
  @Input() predictiveSearchData!: PredictiveSearch;
  @Input() isFullscreen = false;
  @Input() routeIndex = 0;
  @Input() set routePickerError(
    errorField: MultiRoutePickerError[] | undefined
  ) {
    if (errorField && errorField.length > 0) {
      const {
        origin,
        destination,
        sameRoute,
        destinationErrorMessage,
        originErrorMessage,
        sameRouteErrorMessage
      } = errorField[this.routeIndex];
      this.error = {
        origin,
        destination,
        sameRoute,
        destinationErrorMessage,
        originErrorMessage,
        sameRouteErrorMessage
      };
    }
  }

  @Output() swapClicked = new EventEmitter<void>();
  @Output() routePickerClicked = new EventEmitter<SelectedData>();
  @Output() searchModalTriggerClicked = new EventEmitter<SelectedData>();

  @Output() searchModalEvent = new EventEmitter<
    SearchModalEvent<SearchModalEventType>
  >();

  protected handleModalEvents(event: SearchModalEvent<SearchModalEventType>) {
    this.searchModalEvent.emit(event);
  }

  protected handleRoutePickerClick(data: SelectedData) {
    this.modalOpen = true;
    this.lastSearchModalTriggerLocation = data.type as TriggerLocation;
    this.searchModalEvent.emit({ type: 'modalOpen' });
    this.searchModalTriggerClicked.emit(data);
  }

  protected handlePredictiveSearchItemselection(
    eventData: GenericAction<string>
  ) {
    const data: PredictiveSearchItemSelectionEventData = {
      airportCode: eventData.label,
      airportCity: eventData.value ?? '',
      locationType: this.lastSearchModalTriggerLocation as TriggerLocation,
      flightIndex: this.routeIndex
    };

    this.searchModalEvent.emit({
      type: 'predictiveSearchItemSelected',
      data
    });
  }

  protected handleSearchModalClose(searchText: string) {
    if (searchText) {
      this.handlePredictiveSearchItemselection({
        label: searchText.toUpperCase(),
        id: searchText.toUpperCase(),
        value: ''
      });
    }
    this.modalOpen = false;
  }
}
